import Button from "components/buttons/Button";
import cn from 'clsx';
import { IScheduler, schedulerTypes } from "store/scheduler/slice";
import { useTranslation } from "react-i18next";
import styles from './SchedulerTypeTabs.module.scss';
import { MouseEvent, useEffect, useRef, useState } from "react";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import Tooltip from "components/Tooltip";
import React from "react";
import { useDays } from "./useDays";

type Props = {
  activeTab: IScheduler['type'];
  onChangeActiveTab: (v: IScheduler['type']) => void;
  scheduleDraft?: IScheduler;
  onChangeSchedule?: (i: Partial<IScheduler>) => void;
  readOnly?: boolean;
};

export default function CustomMultiTimePicker({
  initialSelectedTime = [],
  onChangeSelectedTime = () => {},
  readOnly = false,
}: {
  initialSelectedTime: string[]
  onChangeSelectedTime?: (v: string[]) => void;
  readOnly?: boolean;
}) {
  const minutes = Array.from({ length: 60 }, (_, idx) =>
    String(idx).padStart(2, "0")
  );
  const hours = Array.from({ length: 24 }, (_, idx) =>
    String(idx).padStart(2, "0")
  );
  const { t } = useTranslation();

  // @ts-ignore
  const [selectingTime, setSelectingTime] = useState<{ hour?: string, minute?: string } | null>(null);
  const [selectedTime, setSelectedTime] = useState<string[]>(initialSelectedTime);

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const timeLabel = (value: string, onDelete: (d: string) => void) => {

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onDelete(value);
    }

    return (
      <Button
        txt={value}
        // @ts-ignore
        handleClick={handleClick}
        className={cn(styles.multitimePickerLabel)}
      />
    )
  };

  const handleDeleteTime = (v: string) => {
    if (readOnly) return;

    const timeToAdd = selectedTime.filter((t => t !== v));

    setSelectedTime(c => c.filter(t => t !== v));
    onChangeSelectedTime(timeToAdd);
  };

  const handleAddTime = () => {
    if (readOnly) return;

    if (!selectingTime || (!selectingTime.hour || !selectingTime.minute)) return;
    const timeToAdd = [
      ...selectedTime,
      `${selectingTime.hour}:${selectingTime.minute}`
    ];
    
    onChangeSelectedTime(timeToAdd);

    setSelectedTime(timeToAdd);

    setSelectingTime(null);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        event.target instanceof Node &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpened(false);
      }
    };

    //@ts-ignore
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      //@ts-ignore
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, []);

  return (
    <div
      className={cn(styles.multitimePickerWrapper)}
      ref={wrapperRef}
    >
        <div className="inner-input__label">
          <span>Specific Times</span>
          <Tooltip text={t("specificTimes")} position='top-right'>
            <button className="info-icon">ℹ</button>
          </Tooltip>
        </div>
      <div
        className={cn(styles.multitimePickerInput, "inner-input", readOnly && styles.readOnly)}
        onClick={() => !readOnly && setIsOpened(prev => !prev)}
      >
        {!selectedTime.length && <>{t("selectTime")}</>}
        {selectedTime.map(v => {
          return <React.Fragment key={v}>{timeLabel(v, handleDeleteTime)}</React.Fragment>
        })}
      </div>
      <div className={cn(styles.multitimePickerDataWrapper, isOpened && "active")}>
        <div className={cn(styles.multitimePicker)}>
          <div className={cn(styles.multitimePickerColumn)}>
            <p className={cn(styles.multitimePickerColumnHeader)}>{t("hours")}</p>
            <div className={cn(styles.multitimePickerDataColumn)}>
              {hours.map(v => (
                <div
                  key={v}
                  className={cn(styles.multitimePickerCell, selectingTime?.hour === v && "active")}
                  onClick={() => {
                    if (readOnly) return;

                    setSelectingTime(c => {
                      if (!c) {
                        return {
                          hour: v
                        }
                      }

                      return {
                        ...c,
                        hour: v
                      }
                    })
                  }}
                >{v}</div>
              ))}
            </div>
          </div>
          <div className={cn(styles.multitimePickerColumn)}>
            <p className={cn(styles.multitimePickerColumnHeader)}>{t("minutes")}</p>
            <div className={cn(styles.multitimePickerDataColumn)}>
              {minutes.map(v => (
                <div
                  key={v}
                  className={cn(styles.multitimePickerCell, selectingTime?.minute === v && "active")}
                  onClick={() => {
                    if (readOnly) return;

                    setSelectingTime(c => {
                      if (!c) {
                        return {
                          minute: v
                        }
                      }

                      return {
                        ...c,
                        minute: v
                      }
                    })
                  }}
                >{v}</div>
              ))}
            </div>
          </div>
        </div>
        <div className={cn(styles.multitimePickerControls)}>
          <Button
            txt={t("cancel")}
            // handleClick={handleApply}
            handleClick={() => {}}
            className={cn(styles.multitimePickerControl)}
            isSecondary
          />
          <Button
            txt={t("add")}
            handleClick={handleAddTime}
            className={cn(styles.multitimePickerControl)}
            isPrimary
            isGreen
            disabled={readOnly || !selectingTime || !selectingTime.hour || !selectingTime.minute}
          />
        </div>
      </div>
    </div>
  )
};

export function SchedulerTypeTabs({
  activeTab,
  onChangeActiveTab,
  scheduleDraft,
  onChangeSchedule = () => { },
  readOnly
}: Props) {
  const { t } = useTranslation();

  const daysWeek = (
    activeDays: number[],
    onChangeActiveDays: (d: number) => void,
  ) => {
    
    const days = useDays({ days: [0, 1, 2, 3, 4, 5, 6] });

    return (
      <div className={cn(styles.schedulerTabsDaysWrapper)}>
        <div className="inner-input__label">
          <span>Days</span>
          <Tooltip text={t("schedulerDays")} position='top-right'>
            <button className="info-icon">ℹ</button>
          </Tooltip>
        </div>
        <div className="row">
          {days.map((v, idx) => (
            <Button
              key={v}
              txt={t(v)}
              // handleClick={handleApply}
              handleClick={() => onChangeActiveDays(idx)}
              className={cn(v === activeTab && 'active')}
              isGreen={activeDays.includes(idx)}
              disabled={readOnly}
            />
          ))}
        </div>
      </div>
    )
  };

  const specificTime = () => {

    const handleChangeSpecificTime = (v: string[]) => {
      onChangeSchedule({
        ...scheduleDraft,
        type: 'specific',
        specific: v,
      })
    };
    
    return (
      <div className="date-picker-wrapper">
        <CustomMultiTimePicker
          initialSelectedTime={scheduleDraft?.type === 'specific' ? scheduleDraft.specific : []}
          onChangeSelectedTime={handleChangeSpecificTime}
          readOnly={readOnly}
          />
      </div>
    )
  }

  const handleChangeActiveDays = (d: number) => {
    if (readOnly) return;

    const daysToAdd = scheduleDraft?.days.includes(d)
      ? scheduleDraft?.days.filter(day => day !== d)
      : [...(scheduleDraft?.days || []), d];

    onChangeSchedule({
      ...scheduleDraft,
      type: activeTab,
      days: daysToAdd
    });
  };

  const handleInterval = (d: number) => {
    if (readOnly || !onChangeSchedule) return;

    if (activeTab === 'interval') {
      onChangeSchedule({
        ...scheduleDraft,
        type: activeTab,
        interval: d
      })
    }
  };

  const renderTab = () => {
    if (activeTab === 'interval') {

      return (
        <>
          <InnerInput
            value={scheduleDraft?.type === 'interval' ? scheduleDraft.interval.toString() : '1'}
            placeholder={t("intervalMinutes")}
            label={t("intervalMinutes")}
            tooltipText={t("setIntervalMinutes")}
            tooltipPosition="right"
            type="number"
            min={0}
            id="intervalMinutes"
            onChange={(e) => handleInterval(+e)}
            disabled={readOnly}
          />
          {daysWeek(scheduleDraft?.days || [], handleChangeActiveDays)}
        </>
      )
    }


    return (
      <>
        {specificTime()}
        {daysWeek(scheduleDraft?.days || [], handleChangeActiveDays)}
      </>
    )
  };

  return (
    <div className={cn(styles.schedulerTabsWrapper)}>
      <div className={cn(styles.schedulerTabs)}>
        <div className={cn(styles.schedulerTabButtons)}>
          {schedulerTypes.map(v => (
            <Button
              key={v}
              txt={t(v)}
              handleClick={() => onChangeActiveTab(v)}
              className={cn(v === activeTab && 'active')}
              disabled={readOnly}
            />
          ))}
        </div>
      </div>
      {renderTab()}
    </div>
  )
};
